import React, {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
} from 'react';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';

import { icon_search } from '@gn-audio/icons';

import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { JabraIcon } from '../../JabraIcon/JabraIcon';

type Props = {
  inputPlaceholderText: string;
  searchQuery: string;
  busy?: boolean;
  showSearchButton?: boolean;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  autofocus?: boolean;
};

export const SearchMenuInput = ({
  inputPlaceholderText,
  searchQuery,
  busy = false,
  setSearchQuery,
  showSearchButton = false,
  autofocus = true,
}: Props): JSX.Element => {
  const router = useRouter();
  const queryParam = router.query.q as string;
  const ref = useRef<HTMLInputElement>(null);
  const { t, getURLWithLocalePrefix } = useFallbackTranslation();
  useEffect(() => {
    if (queryParam !== undefined) {
      setSearchQuery(queryParam);
    }
  }, [setSearchQuery, queryParam]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const inputValue = (event.target as HTMLInputElement).value;
      // replace to router.push after coexistence period.
      window.location.href = getURLWithLocalePrefix(`/search?q=${inputValue}`);
      // .then(() => {
      //     setSearchQuery(inputValue);
      //   });
    }
  };

  const handleSearch = () => {
    if (!ref.current) return;
    // replace to router.push after coexistence period.
    window.location.href = getURLWithLocalePrefix(`/search?q=${ref.current.value}`);
  };

  useEffect(() => {
    if (autofocus) {
      ref.current?.focus();
    }
  }, [autofocus]);

  const onMouseOver = (e: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
    (e.currentTarget.childNodes[0] as HTMLElement)?.classList.add('yellow');
  };

  const onMouseOut = (e: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
    (e.currentTarget.childNodes[0] as HTMLElement)?.classList.remove('yellow');
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  const isQueryEmpty = isEmpty(searchQuery);

  return (
    <div
      className="relative flex flex-grow items-center overflow-hidden rounded-lg border border-gray-200 bg-white focus-within:border-yellow"
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
    >
      {!showSearchButton && (
        <>
          {busy ? (
            <div className="pl-3 pr-2">
              <div className="m-auto block size-4 animate-spin rounded-full border-2 border-black border-t-[transparent] dark:border-white"></div>
            </div>
          ) : (
            <JabraIcon className="h-6 pl-3" Icon={icon_search} />
          )}
        </>
      )}
      <input
        ref={ref}
        className="search-input border-grey h-10 w-auto flex-grow py-2 pl-4 pr-4 transition-colors duration-500 hover:border-yellow focus:outline-none"
        value={searchQuery || ''}
        aria-label={t('topmenu.SearchInput')}
        placeholder={inputPlaceholderText}
        type="search"
        onKeyDown={handleKeyDown}
        onChange={handleChange}
      />

      {showSearchButton && !isQueryEmpty && (
        <button
          className="h-full w-12 bg-gray-200"
          onClick={handleSearch}
          aria-label={t('topmenu.Search')}
        >
          <JabraIcon Icon={icon_search} className="h-6" />
        </button>
      )}
    </div>
  );
};
